(function (root, factory) {
  const createPageTrackingSearch = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = createPageTrackingSearch;
    return;
  }

  if (root) {
    root.createPageTrackingSearch = createPageTrackingSearch;
  }
})(typeof window !== 'undefined' ? window : undefined, function createPageTrackingSearchFactory() {
  return function createPageTrackingSearch(core) {
    const pendingSearchKey = core.pendingSearchKey || 'pageTrackingPendingSearch';
    const pendingPageJumpKey = core.pendingPageJumpKey || 'pageTrackingPendingPageJump';

    function normalizeKeyword(value) {
      return core.utils.fillEmptyValue(value).trim();
    }

    function normalizeSearchType(value) {
      return value === 'searchPage' ? 'searchPage' : 'header_icon';
    }

    function buildPendingSearchState(fields) {
      const currentFields = fields || {};

      return {
        keyword: normalizeKeyword(currentFields.keyword),
        searchType: normalizeSearchType(currentFields.searchType),
        submittedAt: Number(currentFields.submittedAt) || 0
      };
    }

    function savePendingSearch(fields) {
      return core.writeSessionStorage(
        pendingSearchKey,
        JSON.stringify(buildPendingSearchState(fields))
      );
    }

    function readPendingSearch() {
      const rawValue = core.readSessionStorage(pendingSearchKey);

      if (!rawValue) {
        return null;
      }

      try {
        return buildPendingSearchState(JSON.parse(rawValue));
      } catch (error) {
        core.writeSessionStorage(pendingSearchKey, '');
        return null;
      }
    }

    function clearPendingSearch() {
      return core.writeSessionStorage(pendingSearchKey, '');
    }

    function readPendingPageJump() {
      const rawValue = core.readSessionStorage(pendingPageJumpKey);

      if (!rawValue) {
        return null;
      }

      try {
        return JSON.parse(rawValue);
      } catch (error) {
        return null;
      }
    }

    function isTrackableSearchForm(form) {
      return Boolean(
        form &&
        typeof form.matches === 'function' &&
        form.matches('form[data-search-track-source]')
      );
    }

    function resolveSearchFormFromTarget(target) {
      if (!target) {
        return null;
      }

      if (isTrackableSearchForm(target)) {
        return target;
      }

      if (target.form && isTrackableSearchForm(target.form)) {
        return target.form;
      }

      if (typeof target.closest === 'function') {
        const closestForm = target.closest('form[data-search-track-source]');

        if (isTrackableSearchForm(closestForm)) {
          return closestForm;
        }
      }

      return null;
    }

    function getFormKeyword(form) {
      const input = form && typeof form.querySelector === 'function'
        ? form.querySelector('input[name="q"]')
        : null;

      return normalizeKeyword(input && typeof input.value === 'string' ? input.value : '');
    }

    function buildSearchSubmitData(fields, now) {
      const currentFields = fields || {};

      return core.buildDataInfo({
        type: core.config.eventTypes && core.config.eventTypes.searchSubmit,
        keyword: normalizeKeyword(currentFields.keyword),
        searchType: normalizeSearchType(currentFields.searchType),
        quickLink: core.utils.fillEmptyValue(currentFields.quickLink),
        resultCount: Number(currentFields.resultCount) || 0
      }, now);
    }

    function reportQuickLinkClick(label) {
      const keyword = normalizeKeyword(label);

      if (!keyword || !core.canTrack()) {
        return false;
      }

      return core.sendTrackingEvent({
        basicInfo: core.buildBasicInfo(),
        dataInfo: buildSearchSubmitData({
          keyword: keyword,
          searchType: 'header_icon',
          quickLink: keyword,
          resultCount: 0
        }, new Date())
      }, true);
    }

    function resolveFallbackSearchType(pendingPageJump) {
      const referPage = core.utils.fillEmptyValue(pendingPageJump && pendingPageJump.referPage);

      if (!referPage) {
        return 'header_icon';
      }

      try {
        const parsedUrl = new URL(
          referPage,
          core.window.location && core.window.location.origin ? core.window.location.origin : 'https://shop.example'
        );

        return parsedUrl.pathname === '/search' ? 'searchPage' : 'header_icon';
      } catch (error) {
        return 'header_icon';
      }
    }

    function trackSearchFromPendingPageJump() {
      const trackingContext = core.trackingContext || {};
      const pendingPageJump = readPendingPageJump();
      const currentUrl = core.window.location && core.window.location.href ? core.window.location.href : '';
      const currentOrigin = core.window.location && core.window.location.origin ? core.window.location.origin : '';

      if (
        !core.canTrack() ||
        trackingContext.pageType !== 'search' ||
        !trackingContext.searchPerformed ||
        !core.utils.shouldReportPendingPageJump({
          pendingPageJump: pendingPageJump,
          currentUrl: currentUrl,
          nowMs: Date.now(),
          maxAgeMs: core.pendingPageJumpMaxAgeMs,
          currentOrigin: currentOrigin
        })
      ) {
        return false;
      }

      return core.sendTrackingEvent({
        basicInfo: core.buildBasicInfo(),
        dataInfo: buildSearchSubmitData({
          keyword: trackingContext.searchTerms,
          searchType: resolveFallbackSearchType(pendingPageJump),
          quickLink: '',
          resultCount: trackingContext.searchResultsCount
        }, new Date())
      }, true);
    }

    function trackPendingSearchOnResultsPage() {
      const pendingSearch = readPendingSearch();
      const trackingContext = core.trackingContext || {};
      const normalizedSearchTerms = normalizeKeyword(trackingContext.searchTerms);
      const isSearchPage = trackingContext.pageType === 'search';

      if (!core.canTrack() || !isSearchPage) {
        return false;
      }

      if (!pendingSearch) {
        return trackSearchFromPendingPageJump();
      }

      if (!trackingContext.searchPerformed || normalizedSearchTerms !== pendingSearch.keyword) {
        clearPendingSearch();
        return trackSearchFromPendingPageJump();
      }

      clearPendingSearch();

      return core.sendTrackingEvent({
        basicInfo: core.buildBasicInfo(),
        dataInfo: buildSearchSubmitData({
          keyword: trackingContext.searchTerms,
          searchType: pendingSearch.searchType,
          quickLink: '',
          resultCount: trackingContext.searchResultsCount
        }, new Date())
      }, true);
    }

    function handleSearchSubmit(event) {
      const form = resolveSearchFormFromTarget(event && event.target);

      if (!isTrackableSearchForm(form)) {
        return false;
      }

      savePendingSearch({
        keyword: getFormKeyword(form),
        searchType: form.dataset && form.dataset.searchTrackSource,
        submittedAt: Date.now()
      });

      return true;
    }

    function handleSearchInputKeydown(event) {
      const target = event && event.target;
      const form = resolveSearchFormFromTarget(target);

      if (!form || !event || event.key !== 'Enter') {
        return false;
      }

      savePendingSearch({
        keyword: getFormKeyword(form),
        searchType: form.dataset && form.dataset.searchTrackSource,
        submittedAt: Date.now()
      });

      return true;
    }

    function handleQuickLinkClick(event) {
      const target = event && event.target;
      const link = target && typeof target.closest === 'function'
        ? target.closest('[data-search-quick-link]')
        : null;

      if (!link) {
        return false;
      }

      if (typeof link.closest === 'function' && link.closest('.header-v2-search-drawer')) {
        return false;
      }

      return reportQuickLinkClick(link.textContent || '');
    }

    function bindSearchEvents() {
      if (!core.document || typeof core.document.addEventListener !== 'function') {
        return false;
      }

      core.document.addEventListener('submit', handleSearchSubmit, true);
      core.document.addEventListener('keydown', handleSearchInputKeydown, true);
      core.document.addEventListener('click', handleQuickLinkClick, true);
      trackPendingSearchOnResultsPage();
      return true;
    }

    function exposeSearchReporter() {
      if (!core.window) {
        return false;
      }

      core.window.pageTrackingSearch = Object.assign({}, core.window.pageTrackingSearch || {}, {
        reportQuickLinkClick: reportQuickLinkClick
      });

      return true;
    }

    exposeSearchReporter();

    return {
      buildPendingSearchState,
      savePendingSearch,
      readPendingSearch,
      clearPendingSearch,
      readPendingPageJump,
      isTrackableSearchForm,
      resolveSearchFormFromTarget,
      getFormKeyword,
      buildSearchSubmitData,
      reportQuickLinkClick,
      resolveFallbackSearchType,
      trackSearchFromPendingPageJump,
      trackPendingSearchOnResultsPage,
      handleSearchSubmit,
      handleSearchInputKeydown,
      handleQuickLinkClick,
      bindSearchEvents,
      exposeSearchReporter
    };
  };
});
